import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  LayoutDashboard, 
  FileText, 
  Mail, 
  Send, 
  Globe, 
  UserX, 
  Settings, 
  LogOut 
} from 'lucide-react';

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const menuItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/subscribers', icon: UserX, label: 'Subscribers' },
    { path: '/campaigns', icon: Send, label: 'Campaigns' },
    { path: '/templates', icon: FileText, label: 'Templates' },
    { path: '/builder', icon: Mail, label: 'Builder' },
    { path: '/sendgrid', icon: Mail, label: 'SendGrid' },
    { path: '/domains', icon: Globe, label: 'Domains' },
    { path: '/unsubscribe-logs', icon: UserX, label: 'Unsubscribe' },
    { path: '/settings', icon: Settings, label: 'Settings' }
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex min-h-screen bg-[#0B0C10]">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 z-50 w-60 border-r border-[#2A2D36] bg-[#0B0C10] text-[#F3F4F6]">
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-[#2A2D36]">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_mailcraft-13/artifacts/hs5d8bkj_dommo_logo.png" 
                alt="Dommo Logo" 
                className="h-10 w-10 object-contain"
              />
              <div>
                <h1 className="text-xl font-bold tracking-tight text-[#183871]">
                  Dommo
                </h1>
                <p className="text-xs text-[#9CA3AF]">Swiss Mailer</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path || 
                              (item.path === '/builder' && location.pathname.startsWith('/builder'));
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${
                    isActive 
                      ? 'bg-[#183871] text-white shadow-[0_0_15px_rgba(24,56,113,0.3)]' 
                      : 'text-[#9CA3AF] hover:bg-[#1F2128] hover:text-[#F3F4F6]'
                  }`}
                >
                  <Icon size={20} strokeWidth={1.5} />
                  <span className="text-sm font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Logout Button */}
          <div className="p-4 border-t border-[#2A2D36]">
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="flex items-center gap-3 w-full px-3 py-2 rounded-md text-[#9CA3AF] hover:bg-[#1F2128] hover:text-[#F3F4F6] transition-colors"
            >
              <LogOut size={20} strokeWidth={1.5} />
              <span className="text-sm font-medium">Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-60 flex-1 min-h-screen">
        {children}
      </main>
    </div>
  );
};

export default Layout;
